export type LegalSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type LegalBadge = {
  src: string;
  alt: string;
  label: string;
  detail: string;
};

export type LegalPageData = {
  slug:
    | "terms"
    | "privacy"
    | "shariah"
    | "licenses"
    | "terms-of-use"
    | "disclaimer"
    | "conflict-of-interest"
    | "refund-policy";
  eyebrow: string;
  title: string;
  description: string;
  effectiveLabel: string;
  sourceUrl?: string;
  sourceLabel?: string;
  certificateUrl?: string;
  certificateLabel?: string;
  notice?: string;
  heroImage?: {
    src: string;
    alt: string;
  };
  badges?: LegalBadge[];
  sections: LegalSection[];
};

export const LEGAL_LINKS = [
  { label: "Terms of Service", href: "/terms-and-conditions" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Use", href: "/terms-of-use" },
  { label: "Disclaimer", href: "/disclaimer" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Conflict of Interest", href: "/conflict-of-interest" },
  { label: "Shariah Certificate", href: "/shariah" },
  { label: "Licenses", href: "/licenses" },
] as const;

// `slug` on LegalPageData is a stable internal identifier, not the live URL
// (terms/privacy render at /terms-and-conditions and /privacy-policy, the
// already-indexed URLs) — this maps it to the real href for the cross-link
// filter in LegalDocumentPage.
export const LEGAL_SLUG_HREFS: Record<LegalPageData["slug"], string> = {
  terms: "/terms-and-conditions",
  privacy: "/privacy-policy",
  shariah: "/shariah",
  licenses: "/licenses",
  "terms-of-use": "/terms-of-use",
  disclaimer: "/disclaimer",
  "conflict-of-interest": "/conflict-of-interest",
  "refund-policy": "/refund-policy",
};

export const termsData: LegalPageData = {
  slug: "terms",
  eyebrow: "Legal information",
  title: "Terms of Service",
  description:
    "The key conditions that apply when you access the JustGold website, mobile application, and digital gold services.",
  effectiveLabel: "Effective 20 February 2025",
  sections: [
    {
      id: "company",
      title: "Company information",
      paragraphs: [
        "The platform is operated by JST Gold FZCO, registered with DMCC in Dubai, United Arab Emirates. The published license number is DMCC-1019249 and the formation number is DMCC204584.",
        "The registered office is Unit No. 4236, DMCC Business Centre, Level 1, Jewellery & Gemplex 3, Dubai, United Arab Emirates.",
      ],
    },
    {
      id: "eligibility",
      title: "Eligibility and accounts",
      bullets: [
        "Users must be at least 18 years old and legally able to enter into a contract.",
        "Account access may require identity checks and information needed for KYC and AML compliance.",
        "Users are responsible for providing accurate information and protecting their account credentials.",
        "Sanctioned, fraudulent, or otherwise prohibited use is not permitted.",
      ],
    },
    {
      id: "transactions",
      title: "Buying and selling digital gold",
      paragraphs: [
        "Users may buy fractional amounts of digital gold through the platform. Published prices move with market rates and the transaction details shown before confirmation.",
        "Gold may be sold at the prevailing rate. Sale proceeds are paid to the registered bank account after the required compliance checks.",
      ],
    },
    {
      id: "storage",
      title: "Storage and physical redemption",
      bullets: [
        "Digital gold is represented as physically backed gold held in secure custody.",
        "Published terms identify Loomis International as the vault custodian.",
        "Physical delivery may be requested where available, subject to minimum quantities, verification, delivery charges, and applicable logistics conditions.",
      ],
    },
    {
      id: "fees",
      title: "Fees and charges",
      paragraphs: [
        "Trading, physical delivery, authentication, insurance, withdrawal, or bank transfer charges may apply. The currently published terms state that users are not charged a storage fee. Always review the transaction summary in the app before confirming an order.",
      ],
    },
    {
      id: "risk",
      title: "Risk and responsibility",
      paragraphs: [
        "Gold prices can rise or fall. Users remain responsible for their own decisions, account security, and compliance with laws that apply to them.",
        "The published terms limit responsibility for losses linked to market movement, user negligence, technical disruption, third party interruption, and events outside reasonable control.",
      ],
    },
    {
      id: "suspension",
      title: "Suspension and termination",
      paragraphs: [
        "Accounts may be restricted, suspended, or closed where fraud, unlawful activity, sanctions concerns, compliance failures, or a breach of the terms is identified.",
      ],
    },
    {
      id: "law",
      title: "Governing law and updates",
      paragraphs: [
        "The published terms are governed by UAE law and applicable DMCC rules. They also describe dispute resolution through arbitration in Dubai.",
        "The terms may be updated. Continued use after a published change may constitute acceptance, so users should review the current official version periodically.",
      ],
    },
    {
      id: "contact",
      title: "Contact",
      paragraphs: [
        "Questions about these terms can be sent to support@justgold.app or through the Contact Us page.",
      ],
    },
  ],
};

export const privacyData: LegalPageData = {
  slug: "privacy",
  eyebrow: "Your data",
  title: "Privacy Policy",
  description:
    "How JustGold collects, uses, protects, and retains personal information across its website, application, and services.",
  effectiveLabel: "Effective and revised 20 February 2025",
  sections: [
    {
      id: "controller",
      title: "Who controls your data",
      paragraphs: [
        "JST Gold FZCO acts as the data controller for information processed through the website, mobile application, and digital gold platform. The company states that it follows applicable UAE data protection requirements, including Federal Decree Law No. 45 of 2021.",
      ],
    },
    {
      id: "collection",
      title: "Information collected",
      bullets: [
        "Identity and contact details, including name, email, phone number, address, passport, Emirates ID, nationality, and proof of address.",
        "Financial and transaction information needed to process purchases, sales, withdrawals, and account verification.",
        "Device, browser, IP address, usage, authentication, analytics, and cookie information.",
        "Messages, support requests, feedback, and communication preferences.",
      ],
    },
    {
      id: "purpose",
      title: "Why information is used",
      bullets: [
        "To provide the service and execute requested transactions.",
        "To meet legal, KYC, AML, audit, and regulatory obligations.",
        "To prevent fraud, protect accounts, improve the service, and operate the business.",
        "To send marketing where consent or another valid legal basis applies.",
      ],
    },
    {
      id: "legal-basis",
      title: "Legal basis for processing",
      bullets: [
        "Contractual necessity, to deliver the service a user has signed up for.",
        "Legal compliance, including UAE AML and KYC regulations.",
        "Consent, for marketing communications.",
        "Legitimate business interests, including fraud prevention.",
      ],
    },
    {
      id: "sharing",
      title: "Data sharing",
      paragraphs: [
        "The published policy states plainly: \"We do not sell or rent your personal information.\" Information may still be shared when necessary with regulators, payment processors (the policy names Stripe), KYC vendors, hosting and analytics providers, customer support providers, affiliates, business partners, and gold custody or logistics providers, generally under confidentiality agreements.",
      ],
    },
    {
      id: "transfers",
      title: "International transfers",
      paragraphs: [
        "Data may be processed outside the UAE. The published policy describes safeguards for these transfers, including Standard Contractual Clauses (SCCs) with third parties, encryption in transit and at rest, and security assessments of vendors.",
      ],
    },
    {
      id: "rights",
      title: "Your privacy rights",
      bullets: [
        "Request access to or correction of your personal information.",
        "Request deletion where legal and regulatory retention duties allow it.",
        "Ask for processing to be restricted and withdraw consent where applicable.",
        "Change marketing preferences or opt out of promotional messages.",
      ],
    },
    {
      id: "retention",
      title: "Retention",
      paragraphs: [
        "The published policy states that KYC and AML records may be kept for five years after account closure, transaction information for seven years for tax and audit purposes, and marketing information until consent is withdrawn. Legal obligations may require longer retention in specific cases.",
      ],
    },
    {
      id: "cookies",
      title: "Cookies and analytics",
      paragraphs: [
        "Cookies may support authentication, preferences, personalization, and traffic analysis. Browser controls and available consent settings can be used to manage cookies.",
      ],
    },
    {
      id: "security",
      title: "Security and children",
      paragraphs: [
        "The published policy describes AES-256 encryption for sensitive data, role-based access controls, secure infrastructure, and annual penetration testing. No internet transmission or storage system can be guaranteed to be completely secure.",
        "The services are not directed to people under 18, and JustGold states that it does not knowingly collect personal data from children.",
      ],
    },
    {
      id: "privacy-contact",
      title: "Requests and complaints",
      paragraphs: [
        "Privacy requests can be directed to JustGold's Data Protection Officer at support@justgold.app, or by post to JST Gold FZCO, Unit No. 4236, DMCC Business Centre, Level 1, Jewellery & Gemplex 3, Dubai, UAE, or by phone at +971 589361909. The published policy also identifies the UAE Data Office as a route for eligible complaints.",
      ],
    },
  ],
};

export const termsOfUseData: LegalPageData = {
  slug: "terms-of-use",
  eyebrow: "Using the platform",
  title: "Terms of Use",
  description:
    "The rules that govern how you may use the JustGold website, mobile application, and digital gold platform.",
  effectiveLabel: "Effective 20 February 2025",
  sections: [
    {
      id: "company",
      title: "Company information",
      paragraphs: [
        "The platform is operated by JST Gold FZCO, registered with DMCC in Dubai, United Arab Emirates. The published license number is DMCC-1019249 and the formation number is DMCC204584.",
        "The registered office is Unit No. 4236, DMCC Business Centre, Level 1, Jewellery & Gemplex 3, Dubai, United Arab Emirates.",
      ],
    },
    {
      id: "definitions",
      title: "Key terms",
      bullets: [
        "Digital Gold: fractionalized physical gold purchased, held, and traded on the platform.",
        "Gold Reserves: the physical gold backing Digital Gold, held securely with the custodian, Loomis International.",
        "Vault: the high-security facility where the Gold Reserves are held.",
        "Platform: the JustGold website and mobile application through which users transact.",
      ],
    },
    {
      id: "eligibility",
      title: "Eligibility and account registration",
      paragraphs: [
        "To use the platform, users must be at least 18 years old and legally able to enter into contracts, complete the required identity verification under UAE AML rules, provide accurate personal and financial details, and keep their login credentials confidential.",
      ],
      bullets: [
        "Access is not available to individuals or entities on sanctions lists.",
        "Fraudulent, illicit, or money-laundering activity is not permitted.",
        "Access may be restricted in jurisdictions where JustGold is not authorized to operate.",
      ],
    },
    {
      id: "acceptable-use",
      title: "Acceptable use",
      bullets: [
        "No unauthorized or fraudulent transactions.",
        "No use of the platform for money laundering or other illicit activity.",
        "No attempts to bypass system security or access unauthorized data.",
        "No bots or automated tools used to manipulate transactions.",
        "A violation of these rules may lead to immediate account suspension or termination.",
      ],
    },
    {
      id: "transactions",
      title: "Buying, selling, and physical redemption",
      paragraphs: [
        "Users may buy digital gold in fractional units, backed by LBMA-certified physical gold in secure vaults, at real-time market pricing. Gold may be sold at the prevailing rate, with proceeds credited to the user's registered bank account after compliance verification.",
        "Physical delivery of gold holdings can be requested, subject to fees, a minimum threshold, insured logistics partners, and applicable customs rules.",
      ],
    },
    {
      id: "storage",
      title: "Storage and security of gold reserves",
      bullets: [
        "Gold Reserves are held in high-security vaults managed by Loomis International.",
        "Independent audits and regulatory inspections verify gold holdings.",
        "Users can verify their holdings through JustGold's transparency portal.",
      ],
    },
    {
      id: "fees",
      title: "Fees and charges",
      bullets: [
        "Trading fees: a percentage-based fee on each transaction.",
        "Storage fees: none for users.",
        "Physical delivery fees: shipping, authentication, and insurance costs apply.",
        "Withdrawal fees: bank transfer fees based on the withdrawal method selected.",
      ],
    },
    {
      id: "liability",
      title: "Limitation of liability and indemnification",
      paragraphs: [
        "JustGold is not liable for gold price fluctuations, unauthorized access caused by user negligence, or losses from technical failures, third-party service interruptions, or events outside its reasonable control.",
        "Users agree to indemnify JustGold against claims, liabilities, or losses arising from a violation of these Terms, misuse of the platform, or unauthorized access to their account.",
      ],
    },
    {
      id: "termination",
      title: "Termination and suspension",
      paragraphs: [
        "Accounts may be suspended or terminated without notice for fraudulent activity, a violation of UAE law or DMCC regulations, or high-risk financial activity linked to the account.",
      ],
    },
    {
      id: "law",
      title: "Governing law and updates",
      paragraphs: [
        "These Terms are governed by UAE law and DMCC regulations, with disputes resolved through arbitration under DIFC rules.",
        "JustGold may modify these Terms at any time; continued use of the platform after a change implies acceptance of the revised Terms.",
      ],
    },
  ],
};

export const disclaimerData: LegalPageData = {
  slug: "disclaimer",
  eyebrow: "Risk information",
  title: "Disclaimer",
  description:
    "Important information about the nature of JustGold's services, the risks involved, and the limits of the information provided.",
  effectiveLabel: "Effective 20 February 2025",
  sections: [
    {
      id: "no-advice",
      title: "No professional advice",
      paragraphs: [
        "Information provided through the services is for general informational purposes only. It is not investment, financial, tax, or legal advice, and is not a recommendation to buy or sell gold or any other financial instrument, nor tailored to any individual's circumstances.",
      ],
      bullets: [
        "Users should seek independent advice from qualified professionals.",
        "Users should review all relevant offering documents.",
        "Users should conduct their own due diligence before making financial decisions.",
      ],
    },
    {
      id: "forward-looking",
      title: "Forward-looking statements",
      paragraphs: [
        "Any opinions, forecasts, or estimates JustGold shares reflect internal analysis at the time of publication and may change without notice. Past performance is not indicative of future results, and statements about market trends or gold prices are opinions, not guarantees.",
      ],
    },
    {
      id: "risks",
      title: "Risks of digital gold transactions",
      paragraphs: [
        "Digital gold transactions carry inherent risks, including market volatility, regulatory change affecting digital assets, liquidity constraints on holdings, third-party risk from custodians or service providers, and cybersecurity threats. By using the services, users accept these risks.",
      ],
    },
    {
      id: "no-warranty",
      title: "No warranties",
      paragraphs: [
        "The services are provided \"as is\" and \"as available\", without warranties of accuracy, timeliness, completeness, merchantability, fitness for a particular purpose, or uninterrupted, error-free operation.",
      ],
    },
    {
      id: "liability",
      title: "Limitation of liability",
      paragraphs: [
        "To the fullest extent permitted under UAE law, JustGold and its directors, employees, and affiliates are not liable for damages arising from use of the services, reliance on information provided, or cybersecurity incidents, third-party actions, or technical failures. Where liability cannot be excluded, total liability is capped at AED 1,000 or the fees paid by the user in the preceding six months, whichever is lower.",
      ],
    },
    {
      id: "third-party",
      title: "Third-party links and user responsibilities",
      paragraphs: [
        "JustGold may link to third-party platforms for convenience but does not endorse or control their content, privacy practices, or terms — users should review third-party policies independently.",
        "Users agree to comply with applicable UAE and local laws, ensure their use of the services does not violate any regulation, and stay aware of regulatory changes affecting digital gold transactions.",
      ],
    },
    {
      id: "law",
      title: "Amendments, governing law, and severability",
      paragraphs: [
        "This Disclaimer may be updated at any time, with changes effective on posting; continued use of the services constitutes acceptance of the revised version. It is governed by UAE law, with disputes resolved through binding arbitration in Dubai under DIAC rules. If any provision is found unenforceable, the remaining terms stay in full effect.",
      ],
    },
  ],
};

export const conflictOfInterestData: LegalPageData = {
  slug: "conflict-of-interest",
  eyebrow: "Governance",
  title: "Conflict of Interest Policy",
  description:
    "How JustGold identifies, discloses, and manages conflicts of interest across its business.",
  effectiveLabel: "Effective 20 February 2025",
  sections: [
    {
      id: "scope",
      title: "Scope",
      paragraphs: [
        "This policy applies to all employees, officers, directors, and temporary staff, third parties acting on JustGold's behalf (advisors, consultants, contractors, suppliers, agents), and intercompany transactions within the JustGold corporate structure — across all business activities, partnerships, and transactions with clients, vendors, government agencies, investors, and regulators.",
      ],
    },
    {
      id: "definitions",
      title: "Key terms",
      bullets: [
        "Conflict of interest: any situation where personal, financial, or external interests compromise someone's ability to act in JustGold's best interests.",
        "Material interest: a direct or indirect financial stake exceeding AED 5,000 annually in a competitor, supplier, or client.",
        "Immediate family: spouse, children, parents, siblings, or others in the same household.",
        "Compliance Officer: the designated authority for conflict disclosures and oversight (compliance@justgold.app).",
      ],
    },
    {
      id: "identifying",
      title: "Identifying conflicts of interest",
      paragraphs: [
        "Financial conflicts include holding a material interest in a competitor, supplier, or client, receiving gifts or entertainment exceeding AED 1,000 from external parties, or using company assets for personal gain.",
        "Personal and external conflicts include business dealings with immediate family or close associates, holding outside roles that compete with or influence JustGold, and accepting undisclosed commissions or incentives.",
        "Operational and client conflicts include preferential treatment based on personal relationships, misuse of confidential information, and participating in decisions with a personal financial interest.",
        "Intercompany conflicts include favoring affiliated entities without fair market justification.",
      ],
    },
    {
      id: "disclosure",
      title: "Disclosure requirements",
      paragraphs: [
        "Conflicts must be disclosed to the Compliance Officer within 3 business days of identification, in writing, covering the nature of the conflict, the parties involved, the potential impact, and proposed mitigation steps. Failure to disclose may lead to disciplinary action, financial penalties, or legal proceedings.",
      ],
    },
    {
      id: "management",
      title: "Conflict management and record keeping",
      paragraphs: [
        "Once disclosed, the Compliance Officer assesses severity and may apply recusal, information barriers, reassignment, transaction termination, or stakeholder notification. All actions are documented in a Conflict Register for audit purposes.",
        "Disclosures, actions, and resolutions are retained for 5 years post-resolution, accessible only to the Compliance Officer and the Board of Directors.",
      ],
    },
    {
      id: "compliance",
      title: "Compliance and enforcement",
      bullets: [
        "Annual conflict-of-interest declarations are required from employees and relevant third parties.",
        "Conflict management training is provided at onboarding and annually thereafter.",
        "Violations may result in warnings, additional training, termination, contract suspension, financial penalties, or reporting to DMCC authorities depending on severity.",
        "Concerns can be reported via support@justgold.app.",
      ],
    },
    {
      id: "law",
      title: "Policy review and governing law",
      paragraphs: [
        "The Board of Directors reviews this policy annually for effectiveness and compliance with UAE law, with updates communicated via email and internal channels. The policy is governed by UAE law and DMCC regulations.",
      ],
    },
  ],
};

export const refundPolicyData: LegalPageData = {
  slug: "refund-policy",
  eyebrow: "Orders and delivery",
  title: "Refund Policy",
  description:
    "How order confirmation, delivery, and returns work for physical gold purchased through JustGold.",
  effectiveLabel: "Effective 20 February 2025",
  sections: [
    {
      id: "confirmation",
      title: "Order confirmation and cancellation",
      paragraphs: [
        "Once an order to buy or sell gold is confirmed, it is final — orders cannot be refunded or cancelled after confirmation. The gold price is locked in at the time of order placement, so later price movements do not affect the transaction.",
      ],
    },
    {
      id: "delivery",
      title: "Delivery process",
      paragraphs: [
        "Deliveries are made only to the customer's registered address, and proof of identity (such as an Emirates ID) may be required by the courier partner at the time of delivery.",
      ],
      bullets: [
        "If no one is available to receive the delivery, the courier will attempt delivery at least once more before the package is returned to the secure vault.",
        "If a package is returned, the corresponding grams of gold are credited back to the customer's JustGold account, and shipping charges apply again for a new delivery request.",
      ],
    },
    {
      id: "verification",
      title: "Package verification",
      paragraphs: [
        "Customers should verify that a package has not been tampered with and is of acceptable quality before accepting delivery. Once delivery is accepted, returns and refunds are not possible — products are certified, and JustGold guarantees their authenticity and quality.",
      ],
    },
    {
      id: "quality",
      title: "Quality assurance",
      paragraphs: [
        "All gold delivered is certified and hallmarked to verify purity. Any concerns about quality or authenticity should be raised with customer support at support@justgold.app, and will be investigated promptly.",
      ],
    },
    {
      id: "shipping",
      title: "Shipping charges",
      paragraphs: [
        "Shipping charges are non-refundable once delivery has been initiated. If a package is returned due to failed delivery attempts, shipping charges apply again for any subsequent delivery request.",
      ],
    },
  ],
};

export const shariahData: LegalPageData = {
  slug: "shariah",
  eyebrow: "Ethical ownership",
  title: "Shariah Compliance",
  description:
    "The principles behind JustGold’s interest free approach to buying, holding, selling, and redeeming physical gold.",
  effectiveLabel: "Current public compliance overview",
  sourceUrl: "https://justgold.app/",
  sourceLabel: "View JustGold’s official compliance overview",
  certificateUrl: "/shariah-compliance-certificate.pdf",
  certificateLabel: "Download the Shariah Compliance Certificate (PDF)",
  notice:
    "The public site confirms Shariah compliant positioning and recognized scholar guidance. The formal Shariah Compliance Certificate for Just Gold FZCO is available below; contact support if you need help verifying its current scope.",
  heroImage: {
    src: "/bento-shariah.png",
    alt: "JustGold Shariah compliance seal with a gold coin",
  },
  sections: [
    {
      id: "approach",
      title: "The JustGold approach",
      paragraphs: [
        "JustGold presents its gold service as Shariah compliant and interest free. The public site states that the structure follows Islamic finance principles under recognized Shariah guidance.",
      ],
    },
    {
      id: "ownership",
      title: "Physical backing and ownership",
      paragraphs: [
        "Gold bought through the platform is represented as real physical gold held in secure custody. The public site states that customer gold is not lent out or leveraged.",
      ],
    },
    {
      id: "interest",
      title: "No interest based return",
      paragraphs: [
        "The product is described as interest free. Returns are not promised or fixed; the value of a customer’s holding moves with the price of gold and the transaction terms shown in the app.",
      ],
    },
    {
      id: "pricing",
      title: "Clear price and quantity",
      paragraphs: [
        "The app shows the gold quantity, current price, applicable charges, and transaction details before confirmation. This supports clarity between the parties at the point of purchase or sale.",
      ],
    },
    {
      id: "possession",
      title: "Custody and redemption",
      paragraphs: [
        "Gold remains in secure custody until it is sold or redeemed. Physical redemption is available subject to product availability, identity checks, minimum quantities, and delivery conditions.",
      ],
    },
    {
      id: "guidance",
      title: "Scholar guidance and certificate access",
      paragraphs: [
        "For the name of the reviewing body, the certificate date, its validity, and the exact products covered, request the current formal certificate from support@justgold.app.",
      ],
    },
  ],
};

export const licensesData: LegalPageData = {
  slug: "licenses",
  eyebrow: "Company records",
  title: "Licenses and Certifications",
  description:
    "Published registration, membership, and information security credentials associated with JustGold in the UAE.",
  effectiveLabel: "Verified against public JustGold pages",
  sourceUrl: "https://justgold.app/terms-and-conditions",
  sourceLabel: "View official company information",
  notice:
    "Company registration, trade licensing, memberships, and technical certifications serve different purposes. They should not be interpreted as a promise of investment performance or protection from market risk.",
  badges: [
    {
      src: "/logo-iso-27001.png",
      alt: "ISO 27001 certification mark",
      label: "ISO 27001",
      detail: "Information security certification announced by JustGold.",
    },
    {
      src: "/logo-dubai-jewellery.svg",
      alt: "Dubai Jewellery Group logo",
      label: "Dubai Jewellery Group",
      detail: "Public membership number O10080/25.",
    },
  ],
  sections: [
    {
      id: "entity",
      title: "Legal entity",
      bullets: [
        "Company name: JST Gold FZCO.",
        "Formation number: DMCC204584.",
        "Trade license number: DMCC-1019249.",
        "Registration authority: DMCC, Dubai, United Arab Emirates.",
      ],
    },
    {
      id: "office",
      title: "Registered office",
      paragraphs: [
        "Unit No. 4236, DMCC Business Centre, Level 1, Jewellery & Gemplex 3, Dubai, United Arab Emirates.",
      ],
    },
    {
      id: "iso",
      title: "ISO 27001 certification",
      paragraphs: [
        "JustGold’s public website announces ISO 27001 certification for information security. ISO 27001 concerns the management of information security risks and controls; it is not a certification of gold prices or investment returns.",
      ],
    },
    {
      id: "membership",
      title: "Dubai Jewellery Group membership",
      paragraphs: [
        "JustGold’s public pages display Dubai Jewellery Group membership number O10080/25. Industry membership is distinct from company licensing and should be understood in that context.",
      ],
    },
    {
      id: "custody",
      title: "Custody and operational partners",
      paragraphs: [
        "The official terms identify Loomis International as the custodian for physical gold reserves. A service provider relationship is separate from the company’s trade license and technical certifications.",
      ],
    },
    {
      id: "verification",
      title: "Verify a current record",
      paragraphs: [
        "Registration and certification status can change. For current copies, issuing body details, certificate scope, and validity dates, contact support@justgold.app before relying on a credential.",
      ],
    },
  ],
};
